import { usePlatform } from "@core-ui/hooks/usePlatform";
import { cn } from "@core-ui/utils/cn";
import type { AttachFiles } from "@ext/agent/components/hooks/useAttachFiles";
import { useAutoFocusTextarea } from "@ext/agent/components/hooks/useAutoFocusTextarea";
import { ChatInputAttachments } from "@ext/agent/components/panel/ChatInputAttachments";
import { ChatModelMenu } from "@ext/agent/components/panel/ChatModelMenu";
import { ChatToolsMenu } from "@ext/agent/components/panel/ChatToolsMenu";
import { SessionContextUsage } from "@ext/agent/components/panel/SessionContextUsage";
import useChatStore, { setQuote, useChatDraft, useChatQuote } from "@ext/agent/components/store/ChatStore";
import { getAgentMode } from "@ext/agent/core/agentMode";
import type { StoredAttachment } from "@ext/agent/core/attachmentStore";
import type { AiAgentModel } from "@ext/agent/llm";
import t from "@ext/localization/locale/translate";
import { Alert, AlertDescription, AlertIcon } from "@ui-kit/Alert";
import { IconButton } from "@ui-kit/Button";
import { Icon } from "@ui-kit/Icon";
import { Tag } from "@ui-kit/Tag";
import { AutogrowTextarea } from "@ui-kit/Textarea";
import { OverflowTooltip, Tooltip, TooltipContent, TooltipText, TooltipTitle, TooltipTrigger } from "@ui-kit/Tooltip";
import { type ChangeEvent, type KeyboardEvent, useCallback } from "react";

type Props = {
	attach: AttachFiles;
	attachments: StoredAttachment[];
	onSubmit: () => void;
	onCancel?: () => void;
	disabled?: boolean;
	sending?: boolean;
	catalogName?: string | null;
	selectedSkillName: string | null;
	browserAllowed: boolean;
	onSkillChange: (name: string | null) => void;
	onBrowserAllowedChange: (allowed: boolean) => void;
	onSkillTagClick?: () => void;
	models: AiAgentModel[];
	modelPickAvailable: boolean;
	onModelChange: (model: AiAgentModel) => void;
};

const ChatInput = ({
	attach,
	attachments,
	onSubmit,
	onCancel,
	disabled,
	sending,
	catalogName = null,
	selectedSkillName,
	browserAllowed,
	onSkillChange,
	onBrowserAllowedChange,
	onSkillTagClick,
	models,
	modelPickAvailable,
	onModelChange,
}: Props) => {
	const { draft: value, onDraftChange: onChange } = useChatDraft();
	const quote = useChatQuote();
	const quotedText = quote?.text;
	const { isTauri } = usePlatform();
	const agentMode = getAgentMode();
	const activeSessionId = useChatStore((s) => s.activeSessionId);
	const containerRef = useAutoFocusTextarea(disabled);
	const { attachFiles, detachFile, isAttaching, fileError, setFileError } = attach;

	const onFileChange = useCallback(
		(e: ChangeEvent<HTMLInputElement>) => {
			const files = Array.from(e.target.files ?? []);
			e.target.value = "";
			attachFiles(files);
		},
		[attachFiles],
	);

	const submit = useCallback(() => {
		if (disabled || isAttaching()) return;
		if (!value.trim() && attachments.length === 0) return;
		onSubmit();
		setFileError(null);
	}, [attachments.length, disabled, isAttaching, onSubmit, setFileError, value]);

	const insertNewline = useCallback(
		(el: HTMLTextAreaElement) => {
			const start = el.selectionStart ?? 0;
			const end = el.selectionEnd ?? 0;
			const next = `${value.slice(0, start)}\n${value.slice(end)}`;
			onChange(next);
			const pos = start + 1;
			requestAnimationFrame(() => {
				el.focus();
				el.setSelectionRange(pos, pos);
			});
		},
		[onChange, value],
	);

	const onKeyDown = useCallback(
		(e: KeyboardEvent<HTMLTextAreaElement>) => {
			if (e.key !== "Enter") return;

			if (e.ctrlKey || e.metaKey) {
				e.preventDefault();
				if (!disabled) insertNewline(e.currentTarget);
				return;
			}

			if (e.shiftKey) {
				return;
			}

			e.preventDefault();
			submit();
		},
		[disabled, insertNewline, submit],
	);

	const hasContent = value.trim().length > 0;
	const isDisabled = sending ? false : !hasContent || disabled;
	const handleClick = sending ? onCancel : submit;
	const sendButtonIcon = sending ? "square" : "arrow-up";

	return (
		<div className="w-full min-w-0">
			{fileError && (
				<Alert className="mb-2" focus="medium" status="error">
					<AlertIcon icon="alert-circle" />
					<AlertDescription>{fileError}</AlertDescription>
					<Tooltip>
						<TooltipTrigger asChild>
							<IconButton
								className="col-start-3 p-0.5 h-5"
								icon="x"
								iconClassName="size-3.5"
								onClick={() => setFileError(null)}
								size="xs"
								status="error"
								variant="ghost"
							/>
						</TooltipTrigger>
						<TooltipContent>{t("agent.tooltips.close")}</TooltipContent>
					</Tooltip>
				</Alert>
			)}
			{quotedText && (
				<div className="flex items-center gap-1.5 pl-3 pr-2 py-1.5 rounded-t-xl border-primary-border border-[0.5px] border-b-secondary-border bg-background">
					<Icon className="size-3.5 shrink-0 text-muted-foreground" icon="corner-down-right" />
					<OverflowTooltip className="truncate min-w-0 flex-1 text-sm text-muted-foreground">
						{quotedText}
					</OverflowTooltip>
					<Tooltip>
						<TooltipTrigger asChild>
							<IconButton
								className="shrink-0 p-0.5"
								icon="x"
								iconClassName="size-3.5"
								onClick={() => setQuote(null)}
								size="xs"
								variant="ghost"
							/>
						</TooltipTrigger>
						<TooltipContent>{t("agent.tooltips.remove-quote")}</TooltipContent>
					</Tooltip>
				</div>
			)}
			<div
				className={cn(
					"relative w-full flex flex-col gap-1 p-2 bg-background shadow-soft-sm after:absolute after:inset-0 after:pointer-events-none after:rounded-[inherit] after:border-[0.5px] after:border-primary-border after:content-['']",
					quotedText ? "rounded-b-xl after:border-t-0" : "rounded-xl",
				)}
			>
				<div className="flex items-stretch flex-col py-1 px-2" ref={containerRef}>
					{attachments.length > 0 && <ChatInputAttachments attachments={attachments} onRemove={detachFile} />}

					<AutogrowTextarea
						autoFocus
						className={cn(
							"min-h-0 p-0 lg:p-0 rounded-none focus:rounded-none text-sm focus:bg-transparent focus:border-transparent w-full resize-none border-0 bg-transparent text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-0 focus:border-0 shadow-none!",
							"shadow-none shadow-soft-none hover:shadow-soft-none focus:shadow-soft-none active:shadow-soft-none invalid:shadow-soft-none aria-[invalid=true]:shadow-soft-none",
							"hover:shadow-none focus:shadow-none active:shadow-none invalid:shadow-none aria-[invalid=true]:shadow-none",
							"disabled:!border-transparent disabled:!bg-transparent disabled:!shadow-none disabled:opacity-100 disabled:cursor-default",
						)}
						disabled={disabled}
						key={activeSessionId ?? ""}
						maxRows={12}
						minRows={1}
						onChange={(e) => onChange(e.target.value)}
						onKeyDown={onKeyDown}
						placeholder={t("agent.input.placeholder")}
						value={value}
					/>
				</div>

				<div className="flex justify-between items-center gap-1">
					<div className="flex min-w-0 h-7">
						{agentMode === "full" && (
							<div className="shrink-0">
								<ChatToolsMenu
									catalogName={catalogName}
									onFileChange={onFileChange}
									onSkillChange={onSkillChange}
									selectedSkillName={selectedSkillName}
								/>
							</div>
						)}

						<div className="shrink-0 h-7">
							<Tooltip>
								<TooltipTrigger asChild>
									<span
										className={cn("inline-flex", !isTauri && "cursor-not-allowed")}
										tabIndex={!isTauri ? 0 : undefined}
									>
										<IconButton
											aria-label={t("agent.browser.allow")}
											aria-pressed={browserAllowed}
											className="p-1.5"
											disabled={!isTauri}
											icon="globe"
											iconClassName="h-4 w-4"
											onClick={() => onBrowserAllowedChange(!browserAllowed)}
											size="xs"
											status={browserAllowed ? "info" : "default"}
											variant="ghost"
										/>
									</span>
								</TooltipTrigger>
								<TooltipContent>
									<TooltipTitle>{t("agent.browser.tooltip-title")}</TooltipTitle>
									<TooltipText className="block">{t("agent.browser.tooltip-text")}</TooltipText>
									{!isTauri && (
										<TooltipText className="block mt-1">
											{t("agent.browser.tooltip-desktop-only")}
										</TooltipText>
									)}
								</TooltipContent>
							</Tooltip>
						</div>

						{modelPickAvailable && models.length > 0 && (
							<div className="min-w-0">
								<ChatModelMenu disabled={sending} models={models} onModelChange={onModelChange} />
							</div>
						)}

						{selectedSkillName && (
							<div className="flex items-center gap-1 text-primary ml-1 min-w-0">
								<Tag
									buttonClassName="min-w-0 max-w-full overflow-hidden !shadow-none shadow-soft-none hover:!shadow-none hover:shadow-soft-none active:!shadow-none active:shadow-soft-none focus:!shadow-none focus:shadow-soft-none"
									className="min-w-0 max-w-full"
									containerClassName="w-auto min-w-0 max-w-full"
									onClose={() => onSkillChange(null)}
									onLabelClick={onSkillTagClick}
									startIcon="wrench"
									status="info"
								>
									<OverflowTooltip className="truncate h-6 py-1 min-w-0">
										{selectedSkillName}
									</OverflowTooltip>
								</Tag>
							</div>
						)}
					</div>
					<div className="flex items-center gap-1 shrink-0">
						<SessionContextUsage sending={sending} />

						<Tooltip>
							<TooltipTrigger asChild>
								<IconButton
									className="h-auto"
									disabled={isDisabled}
									icon={sendButtonIcon}
									iconClassName="h-4 w-4"
									onClick={handleClick}
									size="xs"
								/>
							</TooltipTrigger>
							<TooltipContent>{sending ? t("cancel") : t("send")}</TooltipContent>
						</Tooltip>
					</div>
				</div>
			</div>
		</div>
	);
};

export { ChatInput };
